import { useRef, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Lock } from 'lucide-react';
import { type TicketMessage } from '@/hooks/useTickets';
import { AttachmentPreview } from './AttachmentPreview';
import { supabase } from '@/integrations/supabase/client';

interface TicketThreadProps {
  messages: TicketMessage[];
  currentUserType: 'client' | 'admin';
}

export function TicketThread({ messages, currentUserType }: TicketThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Generate fresh signed URLs for attachments at render time
  useEffect(() => {
    const generateUrls = async () => {
      const urlMap: Record<string, string> = {};
      for (const msg of messages) {
        if (msg.attachment_url && !msg.attachment_url.startsWith('http')) {
          // New path-based attachment -- generate fresh signed URL (1 hour)
          const { data } = await supabase.storage
            .from('support-attachments')
            .createSignedUrl(msg.attachment_url, 3600);
          if (data?.signedUrl) urlMap[msg.id] = data.signedUrl;
        } else if (msg.attachment_url) {
          // Legacy signed URL (already stored) -- use as-is
          urlMap[msg.id] = msg.attachment_url;
        }
      }
      setSignedUrls(urlMap);
    };
    generateUrls();
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm py-12">
        No messages yet
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((msg) => {
        const isOwnMessage = msg.sender_type === currentUserType;

        return (
          <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] ${
              msg.is_internal
                ? 'bg-amber-500/10 border border-amber-500/30'
                : isOwnMessage
                  ? 'bg-primary/10 border border-primary/20'
                  : 'bg-muted/50 border border-border'
            } rounded-lg px-4 py-3`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium">{msg.sender_name}</span>
                {msg.is_internal && <Lock className="w-3 h-3 text-amber-500" />}
                <span className="text-xs text-muted-foreground">
                  {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
              {signedUrls[msg.id] && msg.attachment_name && (
                <AttachmentPreview url={signedUrls[msg.id]} name={msg.attachment_name} />
              )}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
